<template>
  <div>
    <transition name="fade">
      <div class="w-full h-screen min-h-screen text-gray-700">
        <div
          class="bg-center bg-cover w-full overflow-hidden"
          style="height: 450px"
          :style="getBackgroundImage()"
        />

        <div class="">
          <div
            class="ml-auto mr-auto -m-36 pl-2 pr-2 md:p-0"
            :class="!compareOwnFlexi ? 'max-w-4xl' : 'max-w-6xl'"
          >
            <div class="shadow">
              <div class="bg-gradient-to-b from-gray-700 to to-gray-900 px-9 py-8">
                <div class="flex justify-between items-center mb-4">
                  <div class="text-4xl font-bold text-white heading leading-none">
                    Flexible Membership Profit Simulator
                    <div class="flex items-center mt-2 text-gray-200">
                      <div
                        v-if="existingFlexibleMembership"
                        class="text-base font-medium"
                      >
                        For clubs with an existing flexible membership
                      </div>
                      <div
                        v-else
                        class="text-base font-medium"
                      >
                        For clubs without an existing flexible membership
                      </div>
                      <div
                        class="text-sm bg-gray-300 text-gray-600 hover:bg-gray-100 hover:text-gray-800 select-none cursor-pointer duration-500 transition-all ml-2 p-1 pl-2 pr-2 flex justify-center items-center leading-none"
                        @click="switchVersion()"
                      >
                        Change
                      </div>
                    </div>
                  </div>

                  <div>
                    <div class="flex">
                      <div class="w-4 h-4" />
                      <div class="w-4 h-4 bg-pmg-400" />
                      <div class="w-4 h-4" />
                    </div>
                    <div class="flex">
                      <div class="w-4 h-4 bg-pmg-400" />
                      <div class="w-4 h-4 bg-pmg-800" />
                      <div class="w-4 h-4 bg-pmg-100" />
                    </div>
                    <div class="flex">
                      <div class="w-4 h-4" />
                      <div class="w-4 h-4 bg-pmg-100" />
                      <div class="w-4 h-4" />
                    </div>
                  </div>
                </div>

                <div class="text-sm text-gray-200 mt-6 leading-relaxed">
                  <div v-if="compareOwnFlexi">
                    Explore the possible outcomes of your own flexible membership platform using our
                    simulator and compare the results to what you could achieve with
                    <a
                      href="https://playmore.golf"
                      target="_blank"
                      class="hover:text-white cursor-pointer transition-all duration-500 font-semibold"
                    >
                      PlayMoreGolf
                    </a>
                    .
                  </div>
                  <div v-else>
                    Discover the possible profit opportunity with launching your own
                    <a
                      href="https://playmore.golf"
                      target="_blank"
                      class="hover:text-white cursor-pointer transition-all duration-500 font-semibold"
                    >
                      PlayMoreGolf
                    </a>
                    flexible membership category.
                  </div>
                </div>
              </div>

              <div
                v-if="compareOwnFlexi"
                class="flex justify-between items-center z-50 p-2 pr-8 pl-8 text-gray-500 bg-white text-lg shadow border-l border-r border-b"
              >
                <div>
                  <div
                    title="Reset form"
                    class="cursor-pointer hover:text-gray-700 transition-all duration-500"
                    style="font-size: 0.97rem; margin-top: 2px; margin-bottom: -2px"
                    @click="reset()"
                  >
                    <i class="fas fa-undo leading-none" />
                  </div>
                </div>

                <div class="flex items-center">
                  <div
                    title="Copy link to clipboard"
                    class="mr-1 cursor-pointer hover:text-gray-700 transition-all duration-500"
                    style="font-size: 0.95rem; margin-bottom: 0px; padding-right: 5px"
                    @click="copyLink()"
                  >
                    <i class="fas fa-link leading-none" />
                  </div>
                  <a
                    :href="
                      'mailto:?subject=PlayMoreGolf membership platform simulator&body=' +
                        encodeURI(getLink())
                    "
                  >
                    <div
                      title="Send via email"
                      class="ml-1 cursor-pointer hover:text-gray-700 transition-all duration-500"
                    >
                      <i class="fas fa-envelope leading-none" />
                    </div>
                  </a>
                </div>
              </div>

              <div class="p-8 pr-7 border border-t-0 bg-white b-lg">
                <div class="flex">
                  <div
                    class="flex pr-6"
                    :class="!compareOwnFlexi ? 'w-full min-w-full' : 'w-2/3'"
                  >
                    <div class="w-1/2 pr-6">
                      <div class="mb-10 text-gray-500 focus-within:text-gray-800">
                        <div class="text-sm font-medium transition-all duration-500">
                          <div
                            v-if="flexiJoinersValid !== true"
                            class="-mt-5 text-sm text-red-600"
                          >
                            {{ flexiJoinersValid }}
                          </div>
                          <span v-if="existingFlexibleMembership">Current flexi members</span>
                          <span v-else>Number of expected flexi members</span>
                          <div
                            class="inline-block tool"
                            :data-tooltip="getFlexiMembersInfoText()"
                          >
                            <i
                              class="fad fa-info-circle duration-250 transition-all cursor-pointer"
                            />
                          </div>
                        </div>
                        <div
                          class="pt-1 pb-1 border-b focus-within:border-b-2 transition-all duration-500 focus-within:border-secondary-100"
                        >
                          <input
                            v-model="currentFlexiMembers"
                            type="number"
                            class="outline-none text-gray-700 group-focus:text-gray-900 text-lg w-full"
                          />
                        </div>
                      </div>

                      <div
                        v-if="compareOwnFlexi"
                        class="mb-10 text-gray-500 focus-within:text-gray-800"
                      >
                        <div class="text-sm font-medium transition-all duration-500">
                          Employee hourly cost
                          <div
                            class="inline-block tool"
                            data-tooltip="The hourly cost of an employee including national insurance and pension contributions."
                          >
                            <i
                              class="fad fa-info-circle duration-250 transition-all cursor-pointer"
                            />
                          </div>
                        </div>
                        <div
                          class="flex pt-1 pb-1 text-gray-600 focus-within:text-gray-900 text-lg border-b focus-within:border-b-2 transition-all duration-500 focus-within:border-secondary-100"
                        >
                          <div>£{{ hourlyRate }}</div>
                        </div>
                      </div>

                      <div class="mb-10 text-gray-500 focus-within:text-gray-800">
                        <div class="text-sm font-medium transition-all duration-500">
                          Years
                          <div
                            class="inline-block tool"
                            data-tooltip="Length of term of the partnership agreement."
                          >
                            <i
                              class="fad fa-info-circle duration-250 transition-all cursor-pointer"
                            />
                          </div>
                        </div>
                        <div
                          class="flex pt-1 pb-1 text-gray-600 focus-within:text-gray-900 text-lg border-b focus-within:border-b-2 transition-all duration-500 focus-within:border-secondary-100"
                        >
                          <input
                            v-model.number="years"
                            min="2"
                            max="3"
                            type="number"
                            class="outline-none text-gray-700 group-focus:text-gray-900 text-lg w-full"
                          />
                        </div>
                      </div>
                    </div>

                    <div class="w-1/2">
                      <div class="mb-10">
                        <div class="text-sm font-medium text-gray-500 focus-within:text-gray-800">
                          <span v-if="existingFlexibleMembership">
                            Current flexible membership price
                          </span>
                          <span v-else>Full 7 day membership price</span>
                          <div
                            class="inline-block tool"
                            data-tooltip="We will use your full 7 day membership price to determine an appropriate flexible membership price for our platform."
                          >
                            <i
                              class="fad fa-info-circle duration-250 transition-all cursor-pointer"
                            />
                          </div>
                        </div>
                        <div
                          class="flex pt-1 pb-1 text-gray-600 focus-within:text-gray-900 text-lg border-b focus-within:border-b-2 transition-all duration-500 focus-within:border-secondary-100"
                        >
                          <div>£</div>
                          <input
                            v-model="membershipCost"
                            type="number"
                            class="outline-none w-full"
                          />
                        </div>
                      </div>

                      <div
                        v-if="compareOwnFlexi"
                        class="text-gray-500 focus-within:text-gray-800 mb-10"
                      >
                        <div class="text-sm font-medium transition-all duration-500">
                          <div
                            v-if="socialSpendValid !== true"
                            class="-mt-5 text-sm text-red-600"
                          >
                            {{ socialSpendValid }}
                          </div>
                          Monthly digital marketing spend
                          <div
                            class="inline-block tool"
                            data-tooltip="Your total monthly budget on social media including paid adverts/boosted posts and agency costs."
                          >
                            <i
                              class="fad fa-info-circle duration-250 transition-all cursor-pointer"
                            />
                          </div>
                        </div>
                        <div
                          class="flex pt-1 pb-1 text-gray-600 focus-within:text-gray-900 text-lg border-b focus-within:border-b-2 transition-all duration-500 focus-within:border-secondary-100"
                        >
                          <div>£</div>
                          <input
                            v-model="ownFlexi.marketingSpendMonth"
                            type="number"
                            class="outline-none w-full"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div
                    class="pl-0"
                    :class="!compareOwnFlexi ? 'w-0' : 'w-1/3'"
                  >
                    <div
                      v-if="compareOwnFlexi"
                      class="mb-10 text-gray-500 focus-within:text-gray-800"
                    >
                      <div class="text-sm font-medium transition-all duration-500">
                        <span v-if="existingFlexibleMembership">Current</span>
                        Membership Management Software Cost
                        <div
                          class="inline-block tool"
                          :data-tooltip="getSoftwareTooltip()"
                        >
                          <i
                            class="fad fa-info-circle duration-250 transition-all cursor-pointer"
                          />
                        </div>
                      </div>
                      <div
                        class="flex pt-1 pb-1 text-gray-600 focus-within:text-gray-900 text-lg border-b focus-within:border-b-2 transition-all duration-500 focus-within:border-secondary-100"
                      >
                        <div>£</div>
                        <input
                          v-model="ownFlexi.licenceFee"
                          type="number"
                          class="outline-none w-full"
                        />
                      </div>
                    </div>

                    <div
                      v-if="compareOwnFlexi"
                      class="my-10 text-gray-500 focus-within:text-gray-800"
                    >
                      <div class="text-sm font-medium transition-all duration-500">
                        <div
                          v-if="marketingSpendValid !== true"
                          class="-mt-5 text-sm text-red-600"
                        >
                          {{ marketingSpendValid }}
                        </div>
                        Monthly other marketing costs
                        <div
                          class="inline-block tool"
                          data-tooltip="Your total monthly budget for all other marketing activities including printed adverts, design work etc."
                        >
                          <i
                            class="fad fa-info-circle duration-250 transition-all cursor-pointer"
                          />
                        </div>
                      </div>
                      <div
                        class="flex pt-1 pb-1 text-gray-600 focus-within:text-gray-900 text-lg border-b focus-within:border-b-2 transition-all duration-500 focus-within:border-secondary-100"
                      >
                        <div>£</div>
                        <input
                          v-model="ownFlexi.marketingSpendOtherMonth"
                          type="number"
                          class="outline-none text-gray-700 group-focus:text-gray-900 text-lg w-full"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div class="mt-10">
                  <div
                    class="w-full min-w-full text-sm p-4 pl-4 pr-4 text-center font-medium transition-all duration-500 text-white select-none cursor-pointer uppercase"
                    :class="
                      validForm
                        ? 'bg-secondary-100 hover:bg-secondary-200'
                        : 'bg-gray-300 text-gray-700 cursor-not-allowed'
                    "
                    @click="simulate()"
                  >
                    Run our membership platform simulator
                    <span v-if="clubName !== null">for {{ clubName }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div
            class="p-2 text-2xl text-center mt-40 mb-6 transition-all duration-500 linear-wipe font-bold"
            :class="loading ? 'opacity-100' : 'opacity-0'"
          >
            Running 20,000 simulations
          </div>

          <div v-if="compareOwnFlexi">
            <Chart
              :comparison-results="comparisonResults"
              :years="years"
              :selected-tab="selectedTab"
            />
          </div>

          <div
            v-if="comparisonResults !== null && comparisonResults !== undefined && compareOwnFlexi"
            class="max-w-4xl ml-auto mr-auto mt-12 pb-12"
          >
            <Table
              :comparison-results="comparisonResults"
              :year-index="yearIndex"
              :years="years"
              :flexible-package-cost="flexiblePackageCost"
              :link="encodeURI(getLink())"
              :selected-tab-prop="selectedTab"
              @incrementYear="nextYear()"
              @decrementYear="previousYear()"
              @selectTab="selectTab"
            />
          </div>

          <div v-if="!compareOwnFlexi">
            <TableNoComparison
              :comparison-results="comparisonResults"
              :year-index="yearIndex"
              :years="years"
              :flexible-package-cost="flexiblePackageCost"
              :link="encodeURI(getLink())"
              :selected-tab-prop="selectedTab"
              @incrementYear="nextYear()"
              @decrementYear="previousYear()"
              @incrementYears="incrementYears()"
              @decrementYears="decrementYears()"
              @selectTab="selectTab"
            />
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
  import { compareFlexibleProducts } from '@/app/shared/services/calculatorService/calculatorService';
  import { submitProspectSimulatorSubmission } from '@/app/shared/services/hubspotService/hubspotService';
  import Chart from '@/app/shared/components/prospect/chart/chart.vue';
  import Table from '@/app/shared/components/prospect/table/table.vue';
  import TableNoComparison from '@/app/shared/components/prospect/table/tableNoComparison.vue';

  export default {
    name: 'Calculator',

    components: {
      Chart,
      Table,
      TableNoComparison,
    },

    props: {
      emailAddress: {
        type: String,
        default: null,
      },
    },

    data() {
      return {
        currency: '£',
        years: 2,
        yearIndex: 0,
        formatter1: '{value}%',
        selectedTab: 'Summary',
        showAllFields: false,
        shareMenuOpen: false,
        cohorts: [320, 400, 448, 480, 560, 720, 800],

        pmgPartnershipFee: 1750,
        membershipCost: 350,
        hourlyRate: 17.92,
        currentFlexiMembers: 50,

        tooltip: { isVisible: true, format: 'C2' },
        ownFlexi: {
          pmgProduct: false,
          licenceFee: 1800,
          emailHours: 2.5,
          retentionModifier: 0.0,
          earningsReceived: 1,
          marketingSpendMonth: 350,
          marketingSpendOtherMonth: 150,
          cardFee: 0.03,
        },

        pmgFlexi: {
          pmgProduct: true,
          licenceFee: 1750,
          emailHours: 0,
          retentionModifier: 0,
          earningsReceived: 0.62,
          marketingSpendMonth: 0,
          marketingSpendOtherMonth: 0,
          flexiblePackageCost: 0,
          cardFee: 0,
        },

        comparisonData: null,
        loading: false,
        linkCopied: false,
        club: null,
      };
    },

    computed: {
      flexiblePackageCost() {
        if (!this.existingFlexibleMembership) {
          const proportionalPrice = this.membershipCost * 0.33;
          return this.cohorts.reduce((prev, curr) =>
            Math.abs(curr - proportionalPrice) < Math.abs(prev - proportionalPrice) ? curr : prev
          );
        }
        return this.membershipCost;
      },

      comparisonResults() {
        if (this.comparisonData === null) {
          return null;
        }

        if (this.compareOwnFlexi) {
          return {
            Product1: this.comparisonData.Product1[this.yearIndex],
            Product2: this.comparisonData.Product2[this.yearIndex],
          };
        }
        return this.comparisonData;
      },

      compareOwnFlexi() {
        return (
          this.$route.params.compareOwnFlexi === 'true'
          || this.$route.params.compareOwnFlexi === true
          || this.existingFlexibleMembership
        );
      },

      existingFlexibleMembership() {
        return (
          this.$route.params.existingFlexibleMembership === 'true'
          || this.$route.params.existingFlexibleMembership === true
        );
      },

      clubName() {
        return this.$route.params.clubName;
      },

      flexiJoinersValid() {
        if (this.currentFlexiMembers >= 200) {
          return 'Value too high';
        }

        return true;
      },

      marketingSpendValid() {
        if (this.ownFlexi.marketingSpendMonth <= 0) {
          return 'Must be greater than 0';
        }
        return true;
      },

      socialSpendValid() {
        if (this.ownFlexi.marketingSpendOtherMonth <= 0) {
          return 'Must be greater than 0';
        }
        return true;
      },

      validForm() {
        if (
          this.flexiJoinersValid === true
          && this.marketingSpendValid === true
          && this.socialSpendValid === true
        ) {
          return true;
        }
        return false;
      },
    },

    watch: {
      flexiblePackageCost() {
        return 0.62;
      },

      linkCopied() {
        setTimeout(() => {
          this.linkCopied = false;
        }, 7000);
      },
    },

    mounted() {
      if (!this.existingFlexibleMembership) {
        this.years = 3;
        this.membershipCost = 1000;
      }
      this.assignParams();
    },

    methods: {
      switchVersion() {
        window.location.href = `/Compare/#/Prospect/${this.clubName}/${!this
          .existingFlexibleMembership}`;
        // window.location.reload();
      },

      getBackgroundImage() {
        if (this.existingFlexibleMembership) {
          return "background-image: url('https://tools.playmore.golf/Compare/img/bg6.jpg')";
        }
        return "background-image: url('https://tools.playmore.golf/Compare/img/bg2.jpg')";
      },

      getSoftwareTooltip() {
        if (this.existingFlexibleMembership) {
          return 'The total cost for the software you currently use to manage your own flexible membership.';
        }

        return 'The total cost for the software to manage your own flexible membership.';
      },

      simulate() {
        if (!this.loading) {
          this.loading = true;
          this.comparisonData = null;

          compareFlexibleProducts(
            this.getOwnFlexi(),
            this.getPMGFlexi(),
            this.years,
            this.existingFlexibleMembership
          ).then((data) => {
            // debugger;
            data.Product1.sort((a, b) => a.Year - b.Year);
            data.Product2.sort((a, b) => a.Year - b.Year);
            setTimeout(() => {
              this.loading = false;
            }, 2000);
            setTimeout(() => {
              this.comparisonData = data;
            }, 2000);

            submitProspectSimulatorSubmission(
              this.emailAddress,
              this.existingFlexibleMembership,
              this.currentFlexiMembers,
              this.ownFlexi.marketingSpendMonth,
              this.ownFlexi.marketingSpendOtherMonth,
              this.ownFlexi.licenceFee,
              this.flexiblePackageCost,
              this.membershipCost,
              this.getLink(),
              data
            ).then(() => {});
          });
        }
      },

      nextYear() {
        if (this.yearIndex + 1 < this.years) {
          this.yearIndex += 1;
        }
      },

      previousYear() {
        if (this.yearIndex > 0) {
          this.yearIndex -= 1;
        }
      },

      incrementYears() {
        if (this.years < 3) {
          this.years += 1;
        }
      },

      decrementYears() {
        if (this.years > 2) {
          this.years -= 1;
        }
      },

      getPMGFlexi() {
        const pmgFlexi = { ...this.pmgFlexi };
        pmgFlexi.flexiblePackageCost = this.flexiblePackageCost;
        pmgFlexi.pmgPartnershipFee = this.pmgPartnershipFee;
        pmgFlexi.hourlyRate = this.hourlyRate;
        pmgFlexi.SocialSpendMonth = this.pmgFlexi.marketingSpendOtherMonth;
        pmgFlexi.currentFlexiMembers = this.currentFlexiMembers;
        pmgFlexi.fullMemberCost = this.membershipCost;
        return pmgFlexi;
      },

      getOwnFlexi() {
        const ownFlexi = { ...this.ownFlexi };
        ownFlexi.flexiblePackageCost = this.flexiblePackageCost;
        ownFlexi.pmgPartnershipFee = this.pmgPartnershipFee;
        ownFlexi.hourlyRate = this.hourlyRate;
        ownFlexi.SocialSpendMonth = this.ownFlexi.marketingSpendOtherMonth;
        ownFlexi.currentFlexiMembers = this.currentFlexiMembers;
        return ownFlexi;
      },

      assignParams() {
        const { query } = this.$route;
        if (query.showAllFields !== undefined) {
          this.showAllFields = query.showAllFields === 'true';
        }

        if (query.licenseFee !== undefined) {
          this.ownFlexi.licenceFee = parseInt(query.licenseFee, 10);
        }

        if (query.membershipCost !== undefined) {
          this.membershipCost = query.membershipCost;
        }

        if (query.pmgFee !== undefined) {
          this.pmgPartnershipFee = parseInt(query.pmgFee, 10);
        }

        if (query.hourlyRate !== undefined) {
          this.hourlyRate = parseFloat(query.hourlyRate);
        }

        if (query.currentFlexi !== undefined) {
          this.currentFlexiMembers = parseInt(query.currentFlexi, 10);
        }

        if (query.monthlyMarketing !== undefined) {
          this.ownFlexi.marketingSpendMonth = parseInt(query.monthlyMarketing, 10);
        }

        if (query.monthlyMarketingOther !== undefined) {
          this.ownFlexi.marketingSpendOtherMonth = parseInt(query.monthlyMarketingOther, 10);
        }

        // const flexiProportion = urlParams.get('flexiProportion');
        // if (flexiProportion !== null) {
        //   this.flexiblePackageCostProportion = parseInt(flexiProportion, 10);
        // }
      },

      reset() {
        // eslint-disable-next-line no-alert
        const r = window.confirm('Are you sure you want to reset the form to the default values?');

        if (r === true) {
          this.pmgPartnershipFee = 2400;
          this.flexiblePackageCost = 350;
          this.hourlyRate = 17.92;
          if (this.club) {
            this.currentFlexiMembers = this.club.CurrentFlexiMembers;
          }

          this.ownFlexi.licenceFee = 1800;
          this.ownFlexi.marketingSpendMonth = 350;
          this.ownFlexi.marketingSpendOtherMonth = 150;
        }
      },

      copyLink() {
        const textArea = document.createElement('textarea');
        textArea.style.position = 'fixed';
        textArea.style.top = 0;
        textArea.style.left = 0;
        textArea.style.width = '2em';
        textArea.style.height = '2em';
        textArea.style.padding = 0;
        textArea.style.border = 'none';
        textArea.style.outline = 'none';
        textArea.style.boxShadow = 'none';
        textArea.style.background = 'transparent';
        textArea.value = `${process.env.VUE_APP_SITE_URL}#/Prospect/${this.clubName}/${this.existingFlexibleMembership}?currentFlexi=${this.currentFlexiMembers}&licenseFee=${this.ownFlexi.licenceFee}&pmgFee=${this.pmgPartnershipFee}&membershipCost=${this.membershipCost}&monthlyMarketing=${this.ownFlexi.marketingSpendMonth}&monthlyMarketingOther=${this.ownFlexi.marketingSpendOtherMonth}`;
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        try {
          document.execCommand('copy');
          // const msg = successful ? 'successful' : 'unsuccessful';
          this.linkCopied = true;
        } catch (err) {
          // eslint-disable-next-line no-console
          console.log('Oops, unable to copy');
        }
        document.body.removeChild(textArea);
      },

      getLink() {
        const link = `${process.env.VUE_APP_SITE_URL}#/Prospect/${encodeURI(this.clubName)}/${
          this.existingFlexibleMembership
        }?currentFlexi=${this.currentFlexiMembers}&licenseFee=${this.ownFlexi.licenceFee}&pmgFee=${
          this.pmgPartnershipFee
        }&membershipCost=${this.membershipCost}&monthlyMarketing=${
          this.ownFlexi.marketingSpendMonth
        }&monthlyMarketingOther=${this.ownFlexi.marketingSpendOtherMonth}`;
        return link;
      },

      selectTab(tab) {
        this.selectedTab = tab;
      },

      getMembershipPriceInfo() {
        if (this.existingFlexibleMembership) {
          return 'The current price of the flexible membership for your club.';
        }

        return 'The full cost of a 7 day membership at your club. We use this to recommend you a flexible membership price.';
      },

      getFlexiMembersInfoText() {
        if (this.existingFlexibleMembership) {
          return 'The current number of flexible members at your club. On average, our clubs gain 6 new members each month.';
        }

        return 'The number of new flexible members you would expect to sign up in the first year.';
      },
    },
  };
</script>

<style lang="scss">
  .linear-wipe {
    text-align: center;

    background: linear-gradient(to right, #bbd673 20%, #add24e 40%, #add24e 60%, #bbd673 80%);
    background-size: 200% auto;

    color: #000;
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;

    animation: shine 2s linear infinite;
    @keyframes shine {
      to {
        background-position: 200% center;
      }
    }
  }

  .vue-slider-process {
    background-color: #add24e;
  }

  .vue-slider:hover .vue-slider-process {
    transition: all;
    transition-duration: 500ms;
    background-color: #add24e;
  }

  .vue-slider-dot-handle {
    border: 2px solid #add24e;
  }

  .vue-slider:hover .vue-slider-dot-handle:hover {
    border-color: #add24e;
  }

  .vue-slider-dot-handle-focus {
    border-color: rgb(248, 168, 74);
    -webkit-box-shadow: 0 0 0 5px rgba(248, 168, 74, 0.2);
    box-shadow: 0 0 0 5px rgba(248, 168, 74, 0.2);
  }
</style>

<style src="../assets/site.css"></style>
